import { FormProvider, useFormState } from 'react-hook-form';
import { DynamicIcon } from 'src/components';
import type { BrandWithSupport } from 'src/graphql';
import { useCgCustomDomains } from 'src/graphql/entities/cg-custom-domain/use-cg-custom-domain';
import { LegalDocumentFields } from 'src/pages/Settings/memberships/components/legal-document/legal-document-fields';
import {
  type LegalDocumentSurface,
  NEW_DOCUMENT,
} from 'src/pages/Settings/memberships/hooks/use-legal-document-surface';
import {
  describeSharedDomainConflict,
  findSharedDomainConflict,
} from 'src/pages/Settings/memberships/utils/find-shared-domain-conflict';
import { useConfirmationModal } from 'src/utils/hooks';

interface LegalDocumentPanelProps {
  surface: LegalDocumentSurface;
  partnerId: number | null;
  selectedSegmentId: string;
  segments: Pick<BrandWithSupport, 'brandId' | 'name'>[];
}

export const LegalDocumentPanel = ({
  surface,
  partnerId,
  selectedSegmentId,
  segments,
}: LegalDocumentPanelProps) => {
  const { confirm } = useConfirmationModal();
  const { domains } = useCgCustomDomains(partnerId);
  const { isDirty } = useFormState({ control: surface.formMethods.control });
  const { savedDocument, documents } = surface;

  const segmentName = (brandId: string) =>
    segments.find((segment) => String(segment.brandId) === brandId)?.name ?? 'Unknown segment';

  const conflict = findSharedDomainConflict(domains, documents, selectedSegmentId);

  const documentOptions = [
    ...documents.map((document) => ({
      value: document.legalDocumentId,
      title: document.name,
    })),
    {
      value: NEW_DOCUMENT,
      title: 'Start a new document',
      icon: <DynamicIcon name="plus" library="lucide" />,
    },
  ];

  const handleDocumentChange = async (documentId: string) => {
    const shouldSwitch =
      !isDirty ||
      (await confirm({
        title: 'Discard unsaved changes?',
        body: "Switching documents discards the changes you haven't saved.",
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
        danger: true,
      }));

    if (shouldSwitch) await surface.selectDocument(documentId);
  };

  return (
    <FormProvider {...surface.formMethods}>
      <LegalDocumentFields
        documentOptions={documentOptions}
        selectedDocumentId={surface.selectedDocumentId}
        onDocumentChange={(documentId) => void handleDocumentChange(documentId)}
        sharedWith={(savedDocument?.brandIds ?? [])
          .filter((brandId) => brandId !== selectedSegmentId)
          .map(segmentName)}
        domainWarning={
          conflict ? describeSharedDomainConflict(conflict, segmentName) : undefined
        }
      />
    </FormProvider>
  );
};
